import { z } from 'zod';

/**
 * Shared Zod schemas for the saved-payment-methods (wallet) feature.
 *
 * PCI posture: Elara stores ONLY an opaque PCI Booking vault token (server-side only) plus non-sensitive
 * display metadata. The token refinements below are the data-entry boundary guard (ELARA-779) — they
 * reject anything shaped like a raw PAN or CVV so a bug or misuse can never persist real card data.
 */

/** Raw PAN: 12-19 digits once separators are stripped. A vault token must never look like this. */
const PAN_LIKE = /^\d{12,19}$/;
/** Raw CVV / CVC: 3-4 digits. */
const CVV_LIKE = /^\d{3,4}$/;

/**
 * The reusable PCI Booking vault token (a.k.a. TokenLocation) returned by the hosted capture iframe.
 * This is the only card credential Elara persists, and it lives in a server-only document. It is never
 * sent to the client.
 */
export const PciVaultTokenSchema = z
  .string()
  .min(8)
  .refine(
    (v) => !PAN_LIKE.test(v.replaceAll(/[\s-]/g, '')),
    'Value looks like a raw card number (PAN); only opaque PCI Booking vault tokens may be stored',
  )
  .refine((v) => !CVV_LIKE.test(v), 'Value looks like a CVV; card security codes must never be stored');

/** Card brand as reported by PCI Booking (`cardType`). Kept permissive so an unexpected brand never blocks a save. */
export const CardBrandSchema = z.string().min(1).describe('Card brand, e.g. Visa, MasterCard, Amex');
export const Last4Schema = z.string().regex(/^\d{4}$/, 'Must be exactly the last four digits').describe('Last four digits of the card');
export const ExpMonthSchema = z.number().int().min(1).max(12).describe('Card expiry month (1-12)');
export const ExpYearSchema = z.number().int().min(2020).max(2099).describe('Card expiry year (YYYY)');

/**
 * Safe, non-sensitive display metadata. Shared by `PaymentMethodModelSpec.shape` and the
 * `AddPaymentMethod` flow input so the contract stays DRY. Contains NO token, PAN, or CVV.
 */
export const PaymentMethodMetadataShape = {
  brand: CardBrandSchema,
  last4: Last4Schema,
  expMonth: ExpMonthSchema,
  expYear: ExpYearSchema,
  cardholderName: z.string().min(1).describe('Name on the card'),
  nickname: z.string().nullish().describe('User-friendly label, e.g. "Personal Visa"'),
} as const;

/**
 * How the traveler is paying for a hotel booking: either a card already saved in their wallet
 * (`saved` — the server resolves the server-only vault token by id) or a freshly captured card
 * (`new` — used for this booking only when not saved). The PAN never appears here; only the opaque
 * PCI Booking vault token plus safe metadata. The token is boundary-checked to reject PAN/CVV shapes.
 *
 * Lives here (rather than in `flows.ts`) so it can be imported from a module that does not collide in
 * the shared declaration output — `flows.ts` re-exports it for the web (ELARA-717).
 */
export const HotelPaymentSelectionSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('saved'), paymentMethodId: z.string() }),
  z.object({
    kind: z.literal('new'),
    pciBookingToken: PciVaultTokenSchema,
    ...PaymentMethodMetadataShape,
  }),
]);
export type HotelPaymentSelection = z.infer<typeof HotelPaymentSelectionSchema>;
